// Epoch 28: Admin Messaging Configuration Component
// Allows admins to configure messaging limits per company
// Date: December 2024

import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase/client';

interface Company {
  id: string;
  name: string;
  domain?: string;
}

interface MessagingConfig {
  id: string;
  company_id: string;
  max_message_exchanges: number;
  warning_at_message_count: number;
  auto_close_after_days: number;
  allow_ai_scoring: boolean;
  created_at: string;
  updated_at: string;
}

const MessagingConfiguration: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [configs, setConfigs] = useState<Record<string, MessagingConfig>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [editingCompany, setEditingCompany] = useState<string | null>(null);
  const [tempConfig, setTempConfig] = useState<Partial<MessagingConfig>>({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load all companies
      const { data: companiesData, error: companiesError } = await supabase
        .from('companies')
        .select('id, name, domain')
        .order('name');

      if (companiesError) {
        console.error('Error loading companies:', companiesError);
        setErrorMessage('Failed to load companies');
        return;
      }

      setCompanies(companiesData || []);

      // Load all messaging configurations
      const { data: configsData, error: configsError } = await supabase
        .from('company_messaging_config')
        .select('*');

      if (configsError) {
        console.error('Error loading configs:', configsError);
        setErrorMessage('Failed to load configurations');
        return;
      }

      // Create a map of company_id to config
      const configMap: Record<string, MessagingConfig> = {};
      configsData?.forEach(config => {
        configMap[config.company_id] = config;
      });
      setConfigs(configMap);
    } catch (error) {
      console.error('Error in loadData:', error);
      setErrorMessage('An error occurred while loading data');
    } finally {
      setLoading(false);
    }
  };

  const getConfigForCompany = (companyId: string): MessagingConfig => {
    return configs[companyId] || {
      id: '',
      company_id: companyId,
      max_message_exchanges: 50,
      warning_at_message_count: 90,
      auto_close_after_days: 7,
      allow_ai_scoring: false,
      created_at: '',
      updated_at: ''
    };
  };

  const handleEdit = (companyId: string) => {
    const config = getConfigForCompany(companyId);
    setEditingCompany(companyId);
    setTempConfig({
      max_message_exchanges: config.max_message_exchanges,
      warning_at_message_count: config.warning_at_message_count,
      auto_close_after_days: config.auto_close_after_days,
      allow_ai_scoring: config.allow_ai_scoring
    });
  };

  const handleCancel = () => {
    setEditingCompany(null);
    setTempConfig({});
  };

  const handleSave = async (companyId: string) => {
    setSaving(companyId);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const existingConfig = configs[companyId];
      
      const configData = {
        company_id: companyId,
        max_message_exchanges: tempConfig.max_message_exchanges || 50,
        warning_at_message_count: tempConfig.warning_at_message_count || 90,
        auto_close_after_days: tempConfig.auto_close_after_days || 7,
        allow_ai_scoring: tempConfig.allow_ai_scoring || false,
        updated_at: new Date().toISOString()
      };

      if (existingConfig) {
        // Update existing config
        const { data, error } = await supabase
          .from('company_messaging_config')
          .update(configData)
          .eq('company_id', companyId)
          .select()
          .single();

        if (error) throw error;

        setConfigs(prev => ({
          ...prev,
          [companyId]: data
        }));
      } else {
        // Create new config
        const { data, error } = await supabase
          .from('company_messaging_config')
          .insert({
            ...configData,
            created_at: new Date().toISOString()
          })
          .select()
          .single();

        if (error) throw error;

        setConfigs(prev => ({
          ...prev,
          [companyId]: data
        }));
      }

      setSuccessMessage(`Configuration saved for ${companies.find(c => c.id === companyId)?.name}`);
      setEditingCompany(null);
      setTempConfig({});
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving config:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to save configuration');
    } finally {
      setSaving(null);
    }
  };

  const handleInputChange = (field: keyof MessagingConfig, value: number | boolean) => {
    setTempConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Messaging Configuration</h2>
        <p className="text-gray-600 mt-2">
          Configure message limits and AI features per company
        </p>
      </div>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-700">{successMessage}</p>
        </div>
      )}

      {errorMessage && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Company
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Max Exchanges
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Warning At
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Auto-Close Days
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                AI Scoring
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {companies.map(company => {
              const config = getConfigForCompany(company.id);
              const isEditing = editingCompany === company.id;
              const displayConfig = isEditing ? { ...config, ...tempConfig } : config;

              return (
                <tr key={company.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {company.name}
                      </div>
                      {company.domain && (
                        <div className="text-sm text-gray-500">
                          {company.domain}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isEditing ? (
                      <input
                        type="number"
                        min="1"
                        max="50"
                        value={displayConfig.max_message_exchanges}
                        onChange={(e) => handleInputChange('max_message_exchanges', parseInt(e.target.value))}
                        className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm"
                      />
                    ) : (
                      <span className="text-sm text-gray-900">
                        {config.max_message_exchanges} exchanges
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isEditing ? (
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={displayConfig.warning_at_message_count}
                        onChange={(e) => handleInputChange('warning_at_message_count', parseInt(e.target.value))}
                        className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm"
                      />
                    ) : (
                      <span className="text-sm text-gray-900">
                        {config.warning_at_message_count} messages
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isEditing ? (
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={displayConfig.auto_close_after_days}
                        onChange={(e) => handleInputChange('auto_close_after_days', parseInt(e.target.value))}
                        className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm"
                      />
                    ) : (
                      <span className="text-sm text-gray-900">
                        {config.auto_close_after_days} days
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isEditing ? (
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={displayConfig.allow_ai_scoring}
                          onChange={(e) => handleInputChange('allow_ai_scoring', e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          {displayConfig.allow_ai_scoring ? 'Enabled' : 'Disabled'}
                        </span>
                      </label>
                    ) : (
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        config.allow_ai_scoring
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {config.allow_ai_scoring ? 'Enabled' : 'Disabled'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {isEditing ? (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleSave(company.id)}
                          disabled={saving === company.id}
                          className="text-green-600 hover:text-green-900 disabled:opacity-50"
                        >
                          {saving === company.id ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={handleCancel}
                          disabled={saving === company.id}
                          className="text-gray-600 hover:text-gray-900 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleEdit(company.id)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-900 mb-2">Configuration Guide:</h3>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• <strong>Max Exchanges:</strong> Maximum number of back-and-forth exchanges (1 exchange = 2 messages)</li>
          <li>• <strong>Warning At:</strong> Number of messages that triggers a warning notification</li>
          <li>• <strong>Auto-Close Days:</strong> Days of inactivity before conversation auto-closes</li>
          <li>• <strong>AI Scoring:</strong> Enable GPT-4o sentiment analysis and lead qualification (adds ~$0.006/conversation)</li>
        </ul>
      </div>
    </div>
  );
};

export default MessagingConfiguration;